import {
  EVENT_TRACKING,
  EVENT_TRACKING_ACTION_START,
  EVENT_TRACKING_ACTION_COMPLETE,
  EVENT_TRACKING_ACTION_PROGRESS,
  EVENT_TRACKING_ACTION_PAUSE,
  EVENT_TRACKING_ACTION_RESUME,
  EVENT_TRACKING_ACTION_MUTE,
  EVENT_TRACKING_ACTION_UNMUTE,
  EVENT_TRACKING_ACTION_SEEK,
  EVENT_TRACKING_ACTION_ADSKIP,
  EVENT_TRACKING_ACTION_DOWNLOAD,
  EVENT_PLAYING,
  EVENT_COMPLETE,
  EVENT_POSITION,
  EVENT_SEEK,
  EVENT_AD_SKIP,
  EVENT_MUTE,
  EVENT_UNMUTE,
  EVENT_PAUSE,
  EVENT_RESUME,
  EVENT_DOWNLOAD,
  METADATA_TYPE_AD_SERVER,
  METADATA_TYPE_AD_CLIENT,
  METADATA_TYPE_AD_VAST,
  METADATA_TYPE_STREAM,
  METADATA_TYPE_ADSWIZZ_STREAM
} from './constants';

/**
 * Tracking event
 *
 * @param  {String} type          The event type
 * @param  {Object} details       The event details
 * @param  {Object} originalEvent The event that caused this event
 */
export function TrackingEvent (type, details, originalEvent) {
  this.type = EVENT_TRACKING;
  this.originalEvent = originalEvent || null;
  this.details = details || {};
  this.details.event = type;
};

/**
 * Check if a tracker is for a live soruce
 *
 * @param {Tracker} tracker
 */
function isLiveTracker (tracker) {
  return [METADATA_TYPE_STREAM, METADATA_TYPE_ADSWIZZ_STREAM].includes(tracker.playlistItem.metadata.contentType);
}

/**
 * Resolve the current position
 *
 * @param  {Tracker} tracker
 * @param  {Object}  context
 * @param  {Object}  originalEvent
 * @return {Number}
 */
function resolvePosition (tracker, context, originalEvent) {
  if (typeof context.position !== 'undefined') {
    return context.position;
  }

  if (typeof originalEvent.position !== 'undefined') {
    return originalEvent.position;
  }

  return tracker.player.getPosition();
}

/**
 * Resolve the current duration
 *
 * @param  {Tracker} tracker
 * @param  {Object}  context
 * @param  {Object}  originalEvent
 * @return {Number}
 */
function resolveDuration (tracker, context, originalEvent) {
  if (isLiveTracker(tracker)) {
    return -1;
  }

  if (typeof context.duration !== 'undefined') {
    return context.duration;
  }

  if (typeof originalEvent.duration !== 'undefined') {
    return originalEvent.duration;
  }

  return tracker.player.getDuration();
}

/**
 * Resolve the current position percent
 *
 * @param  {Tracker} tracker
 * @param  {Object}  context
 * @param  {Object}  originalEvent
 * @return {Number}
 */
function resolvePercent (tracker, context, originalEvent) {
  if (isLiveTracker(tracker)) {
    return 0;
  }

  const position = resolvePosition(tracker, context, originalEvent);
  const duration = resolveDuration(tracker, context, originalEvent);

  return Math.floor(position / (duration * tracker.percentileRatio)) * (tracker.percentileRatio * 100);
}

export const TrackerEventBuilder = {};

/**
 * Builds a tracking event for 'start'
 *
 * @param  {Tracker} tracker A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event that
 * @return {Object}  The tracking event to be fired
 */
TrackerEventBuilder.start = function (tracker, context, originalEvent = null) {
  if (tracker.started) {
    return null;
  }

  tracker.started = true;
  context.position = 0;
  context.percent = 0;

  return new TrackingEvent(EVENT_TRACKING_ACTION_START, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: 0,
    percent: 0,
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

/**
 * Builds a tracking event for 'complete'
 *
 * @param  {Tracker} tracker A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event that
 * @return {Object}  The tracking event to be fired
 */
TrackerEventBuilder.complete = function (tracker, context, originalEvent = null) {
  // prevent tracking complete events if already tracked or playing a live stream
  if (tracker.completed || isLiveTracker(tracker)) {
    return null;
  }

  const duration = resolveDuration(tracker, context, originalEvent);
  context.duration = duration;
  context.position = duration;
  context.percent = 100;
  tracker.completed = true;

  return new TrackingEvent(EVENT_TRACKING_ACTION_COMPLETE, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: duration,
    duration: duration,
    percent: 100,
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

/**
 * Builds a tracking event for 'position'
 *
 * @param  {Tracker} tracker A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event that
 * @return {Object}  The tracking event to be fired
 */
TrackerEventBuilder.position = function (tracker, context, originalEvent = null) {
  let position = resolvePosition(tracker, context, originalEvent);

  if (isLiveTracker(tracker)) {
    context.position = position = Math.floor(position / tracker.percentileSeconds) * tracker.percentileSeconds;

    if (position === 0 || tracker.percentiles[position]) {
      return null;
    }

    tracker.percentiles[position] = true;
    return new TrackingEvent(EVENT_TRACKING_ACTION_PROGRESS, {
      uid: tracker.uid,
      aduid: tracker.aduid,
      position: position,
      percent: 0,
      playlistItem: tracker.playlistItem
    }, originalEvent);
  }

  const percent = resolvePercent(tracker, context, originalEvent);

  if (tracker.percentiles[percent]) {
    return null;
  }

  if (percent === 0) {
    return TrackerEventBuilder.start(tracker, context, originalEvent);
  }

  if (percent === 100) {
    return TrackerEventBuilder.complete(tracker, context, originalEvent);
  }

  // if the percent calculated is invalid then don't generate a tracking event
  if (percent < 0 || percent > 100) {
    return null;
  }

  context.percent = percent;
  context.position = position;
  tracker.percentiles[percent] = true;
  return new TrackingEvent(EVENT_TRACKING_ACTION_PROGRESS, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: position,
    percent: percent,
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

/**
 * Builds a tracking event for 'pause'
 *
 * @param  {Tracker} tracker A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event that
 * @return {Object}  The tracking event to be fired
 */
TrackerEventBuilder.pause = function (tracker, context, originalEvent = null) {
  tracker.interrupted = true;
  return new TrackingEvent(EVENT_TRACKING_ACTION_PAUSE, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: resolvePosition(tracker, context, originalEvent),
    percent: resolvePercent(tracker, context, originalEvent),
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

/**
 * Builds a tracking event for 'resume'
 *
 * @param  {Tracker} tracker A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event that
 * @return {Object}  The tracking event to be fired
 */
TrackerEventBuilder.resume = function (tracker, context, originalEvent = null) {
  if (!tracker.started) return;
  return new TrackingEvent(EVENT_TRACKING_ACTION_RESUME, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: resolvePosition(tracker, context, originalEvent),
    percent: resolvePercent(tracker, context, originalEvent),
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

/**
 * Builds a tracking event for 'mute'
 *
 * @param  {Tracker} tracker       A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event that
 * @return {Object}  The tracking event to be fired
 */
TrackerEventBuilder.mute = function (tracker, context, originalEvent = null) {
  tracker.interrupted = true;
  return new TrackingEvent(EVENT_TRACKING_ACTION_MUTE, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: resolvePosition(tracker, context, originalEvent),
    percent: resolvePercent(tracker, context, originalEvent),
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

/**
 * Builds a tracking event for 'unmute'
 *
 * @param  {Tracker} tracker A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event that
 * @return {Object}  The tracking event to be fired
 */
TrackerEventBuilder.unmute = function (tracker, context, originalEvent = null) {
  return new TrackingEvent(EVENT_TRACKING_ACTION_UNMUTE, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: resolvePosition(tracker, context, originalEvent),
    percent: resolvePercent(tracker, context, originalEvent),
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

/**
 * Builds a tracking event for 'seek'
 *
 * @param  {Tracker} tracker A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event that
 * @return {Object}  The tracking event to be fired
 */
TrackerEventBuilder.seek = function (tracker, context, originalEvent = null) {
  tracker.interrupted = true;
  return new TrackingEvent(EVENT_TRACKING_ACTION_SEEK, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: resolvePosition(tracker, context, originalEvent),
    percent: resolvePercent(tracker, context, originalEvent),
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

/**
 * Builds a tracking event for 'adskip'
 *
 * @param  {Tracker} tracker       A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event
 * @return {Object}
 */
TrackerEventBuilder.adskip = function (tracker, context, originalEvent = null) {
  tracker.interrupted = true;
  return new TrackingEvent(EVENT_TRACKING_ACTION_ADSKIP, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: resolvePosition(tracker, context, originalEvent),
    percent: resolvePercent(tracker, context, originalEvent),
    duration: resolveDuration(tracker, context, originalEvent),
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

/**
 * Builds a tracking event for 'download'
 *
 * @param  {Tracker} tracker       A tracker object
 * @param  {Object}  context       An object containing the tracking context
 * @param  {Object}  originalEvent The original player event
 * @return {Object}
 */
TrackerEventBuilder.download = function (tracker, context, originalEvent = null) {
  return new TrackingEvent(EVENT_TRACKING_ACTION_DOWNLOAD, {
    uid: tracker.uid,
    aduid: tracker.aduid,
    position: resolvePosition(tracker, context, originalEvent),
    percent: resolvePercent(tracker, context, originalEvent),
    playlistItem: tracker.playlistItem
  }, originalEvent);
};

export function Tracker (player, playlistItem) {
  switch (playlistItem.metadata.contentType) {
    case METADATA_TYPE_AD_SERVER:
    case METADATA_TYPE_AD_CLIENT:
    case METADATA_TYPE_AD_VAST:
      this.aduid = playlistItem.metadata.uid;
      this.percentileRatio = 0.25;
      this.percentileSeconds = 0;
      break;
    case METADATA_TYPE_STREAM:
    case METADATA_TYPE_ADSWIZZ_STREAM:
      this.uid = playlistItem.metadata.uid;
      this.percentileRatio = 0;
      this.percentileSeconds = 60;
      break;
    default:
      this.uid = playlistItem.metadata.uid;
      this.percentileRatio = 0.1;
      this.percentileSeconds = 0;
      break;
  }

  this.player = player;
  this.percentiles = {};
  this.interrupted = false;
  this.started = false;
  this.completed = false;
  this.playlistItem = playlistItem;
}

/**
 * Track a player event
 *
 * @param  {Object} event   An event triggered by the player
 * @param  {Object} context An object containing the tracking context
 */
Tracker.prototype.track = function (event, context) {
  let trackingEvent;

  switch (event.type) {
    case EVENT_PLAYING:
      trackingEvent = TrackerEventBuilder.start(this, context, event);
      break;
    case EVENT_COMPLETE:
      trackingEvent = TrackerEventBuilder.complete(this, context, event);
      break;
    case EVENT_POSITION:
      trackingEvent = TrackerEventBuilder.position(this, context, event);
      break;
    case EVENT_SEEK:
      if (!this.player._isStopping) {
        trackingEvent = TrackerEventBuilder.seek(this, context, event);
      }
      break;
    case EVENT_AD_SKIP:
      trackingEvent = TrackerEventBuilder.adskip(this, context, event);
      break;
    case EVENT_MUTE:
      trackingEvent = TrackerEventBuilder.mute(this, context, event);
      break;
    case EVENT_UNMUTE:
      trackingEvent = TrackerEventBuilder.unmute(this, context, event);
      break;
    case EVENT_PAUSE:
      trackingEvent = TrackerEventBuilder.pause(this, context, event);
      break;
    case EVENT_RESUME:
      trackingEvent = TrackerEventBuilder.resume(this, context, event);
      break;
    case EVENT_DOWNLOAD:
      trackingEvent = TrackerEventBuilder.download(this, context, event);
      break;
  }

  // if there is a trackingEvent for this event, and it's position is valid, trigger it on the player now
  if (trackingEvent && trackingEvent.details.position >= 0) {
    trackingEvent.details.isLive = isLiveTracker(this);
    trackingEvent.context = context;
    trackingEvent.context.event = trackingEvent.details.event;
    // add percent to the tracking event if it hasn't already been
    if (typeof trackingEvent.context.percent === 'undefined') {
      trackingEvent.context.percent = resolvePercent(this, context, event);
    }
    this.player.emit(trackingEvent);
  }
};

export default {
  Tracker,
  TrackingEvent,
  TrackerEventBuilder
};
